import { useEffect, useRef, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import { Bot } from "lucide-react";
import type { AiModerationField, AiModerationRisk } from "../../shared/ai-moderation";
import { api } from "../lib/api";

interface AiTestInput {
  baseUrl: string;
  model: string;
  timeoutSeconds: number;
  apiKey: { action: "keep" | "replace" | "clear"; value?: string };
  moderationFields: AiModerationField[];
  riskCategories: AiModerationRisk[];
}
interface TestResult { ok: boolean; message: string; durationMs?: number; endpoint?: string; model?: string }

export function AiConnectionCheck({ configuration, disabled }: { configuration: AiTestInput; disabled: boolean }) {
  // The settings form keeps the number input as text while it is being edited.
  // Normalize it before fingerprinting and sending so the strict API schema
  // receives a number and a changed timeout invalidates an older result.
  const normalizedConfiguration = { ...configuration, timeoutSeconds: Number(configuration.timeoutSeconds) };
  const fingerprint = JSON.stringify(normalizedConfiguration);
  const request = useRef<AbortController | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ fingerprint: string; value: TestResult } | null>(null);
  useEffect(() => {
    setResult(null);
    setBusy(false);
    return () => { request.current?.abort(); request.current = null; };
  }, [fingerprint]);

  const test = async () => {
    if (request.current) return;
    const controller = new AbortController();
    request.current = controller;
    setBusy(true);
    setResult(null);
    try {
      const value = await api<TestResult>("/admin/integrations/ai/test", { method: "POST", body: fingerprint, signal: controller.signal });
      if (!controller.signal.aborted) setResult({ fingerprint, value });
    } catch (error) {
      if (!controller.signal.aborted) setResult({ fingerprint, value: { ok: false, message: error instanceof Error ? error.message : "连接检测失败，请稍后重试" } });
    } finally {
      if (request.current === controller) { request.current = null; setBusy(false); }
    }
  };
  const current = result?.fingerprint === fingerprint ? result.value : null;
  return <div className="ai-connection-check" aria-busy={busy}>
    <div className="ai-connection-actions">
      <Button type="button" variant="outlined" disabled={disabled || busy || !configuration.baseUrl.trim() || !configuration.model.trim()} onClick={() => void test()} startIcon={busy ? <CircularProgress size={16} color="inherit" /> : <Bot aria-hidden="true" />}>
        {busy ? "正在检测…" : "检测连接"}
      </Button>
      <small>使用当前填写的配置和测试文案；不会保存配置或发布动态。密钥留空时使用已保存的密钥。</small>
    </div>
    <div role="status" aria-live="polite">
      {current ? <Alert severity={current.ok ? "success" : "error"}>
        <strong>{current.message}</strong>
        {current.endpoint ? <div className="ai-connection-details">{current.model} · {((current.durationMs ?? 0) / 1000).toFixed(2)} 秒<br />请求地址：{current.endpoint}</div> : null}
      </Alert> : busy ? <span className="sr-only">正在检测 AI 服务连接，请稍候</span> : null}
    </div>
  </div>;
}
